import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Activity, ArrowLeft, Bot, CalendarClock, Check, Code2, Database, Download, ExternalLink, Eye, Hammer, History, LayoutDashboard, Lightbulb, PanelRightClose, PanelRightOpen, PencilLine, Rocket, ShieldCheck, Trash2, Wand2, XCircle } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { PlatformIcon } from "@ncp/assistant-sdk";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { AuthorPanel } from "@ncp/page-host";
import { Badge, Button, EmptyState, InlineAlert, Skeleton, Tabs, TabsList, TabsTrigger, notifySaveFailed, notifySaved, toast, useConfirm } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { useSurfaceLinks } from "../lib/links";
import { usePage, usePageActions, usePageSuggestions, usePageVersion, type PageDetail } from "../features/pages/api";
import { AgentsTab } from "../features/pages/studio/AgentsTab";
import { CodeTab, type CodeFocus } from "../features/pages/studio/CodeTab";
import { DataTab } from "../features/pages/studio/DataTab";
import { JobsTab } from "../features/pages/studio/JobsTab";
import { PreviewTab } from "../features/pages/studio/PreviewTab";
import { SuggestionsTab } from "../features/pages/studio/SuggestionsTab";
import { UsageTab } from "../features/pages/studio/UsageTab";
import { ValidationTab } from "../features/pages/studio/ValidationTab";
import { VersionsTab, checkTone, statusTone } from "../features/pages/studio/VersionsTab";

type Tab = "preview" | "code" | "data" | "agents" | "jobs" | "versions" | "validation" | "suggestions" | "usage";
const TABS: Array<{ key: Tab; icon: typeof Eye }> = [
  { key: "preview", icon: Eye }, { key: "code", icon: Code2 }, { key: "data", icon: Database }, { key: "agents", icon: Bot }, { key: "jobs", icon: CalendarClock },
  { key: "versions", icon: History }, { key: "validation", icon: ShieldCheck }, { key: "suggestions", icon: Lightbulb }, { key: "usage", icon: Activity },
];

/** Page Studio: one page project with its preview, code, data, agents, jobs, versions, checks, suggestions and usage, and the AI author beside it. */
export function PageStudioPage() {
  const { key = "" } = useParams();
  const page = usePage(key);
  if (page.isLoading) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-[70vh]" /></div>;
  if (page.isError || !page.data) return <Missing pageKey={key} />;
  return <Studio newest={page.data} />;
}

function Missing({ pageKey }: { pageKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBreadcrumbs([{ label: t("admin:nav.pages"), href: "/pages" }, { label: pageKey }]);
  return <EmptyState icon={XCircle} title={t("admin:pageStudio.notFound")} description={t("admin:pageStudio.notFoundBody", { key: pageKey })} action={<Button variant="outline" onClick={() => navigate("/pages")}><ArrowLeft /> {t("admin:common.back")}</Button>} />;
}

function Studio({ newest }: { newest: PageDetail }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = usePermission();
  const { openInAssistant } = useSurfaceLinks();
  const actions = usePageActions();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab | null) ?? "preview";
  const viewing = params.get("version");
  const older = usePageVersion(viewing && viewing !== newest.page.id ? viewing : null);
  const detail = viewing && older.data ? older.data : newest;
  const p = detail.page;
  const canAuthor = can("pages.author");
  const editable = canAuthor && !viewing && p.status === "Draft";
  const [authorOpen, setAuthorOpen] = useState(true);
  const [openFile, setOpenFile] = useState<string | null>(null);
  const [focus, setFocus] = useState<CodeFocus | null>(null);
  const [rev, setRev] = useState(0);
  const [codeDirty, setCodeDirty] = useState(false);
  const suggestions = usePageSuggestions(p.key);
  const waiting = (suggestions.data ?? []).filter((s) => s.status === "Submitted").length;
  const build = params.get("build") === "1";

  const set = useCallback((patch: Record<string, string | null>) => setParams((q) => {
    for (const [k, v] of Object.entries(patch)) { if (v === null) q.delete(k); else q.set(k, v); }
    return q;
  }, { replace: true }), [setParams]);
  const setTab = (next: Tab) => set({ tab: next });

  const name = (locale === "ar" && p.nameAr) || p.name;
  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.pages"), href: "/pages" }, { label: name }], [t, name]));
  useHelpTopic({ title: t("admin:pageStudio.help.title"), docsAnchor: "pages", body: (<><p>{t("admin:pageStudio.help.what")}</p><p>{t("admin:pageStudio.help.author")}</p><p>{t("admin:pageStudio.help.release")}</p></>) });

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["pages"] });
    setRev((r) => r + 1);
  }, [qc]);

  const startDraft = async () => {
    try {
      const d = await actions.draft.mutateAsync(p.key);
      notifySaved(d, t("admin:pageStudio.draftCreated", { version: d.page.version }));
      set({ version: null });
    } catch { /* useSave reported it */ }
  };
  const runBuild = async () => {
    try {
      const r = await actions.build.mutateAsync({ id: p.id, force: true });
      // The build ran: a failed build is its own answer, with the compiler's first error.
      if (r.ok) notifySaved(r, t("admin:pageStudio.built")); else toast.error(t("admin:pageStudio.buildFailed", { message: r.diagnostics.find((d) => d.severity === "error")?.message ?? "" }));
      setRev((x) => x + 1);
    } catch { /* useSave reported it */ }
  };
  const stage = async () => {
    if (!(await confirm({ title: t("admin:pageStudio.stageTitle", { name, version: p.version }), description: t("admin:pageStudio.stageBody"), confirmLabel: t("admin:pageStudio.stage"), irreversible: false }))) return;
    try {
      const r = await actions.stage.mutateAsync(p.id);
      notifySaved(r, { title: t("admin:pageStudio.staged", { title: r.changeSetTitle }), action: { label: t("admin:common.open"), onClick: () => navigate(`/releases/${r.changeSetId}`) } });
    } catch { /* useSave reported it */ }
  };
  const discard = async () => {
    if (!(await confirm({ title: t("admin:pageStudio.discardTitle", { version: p.version }), description: t("admin:pageStudio.discardBody"), confirmLabel: t("admin:pageStudio.discard"), tone: "destructive" }))) return;
    try {
      notifySaved(await actions.discard.mutateAsync(p.id), t("admin:pageStudio.discarded"));
      if (newest.versions.length <= 1) navigate("/pages");
    } catch { /* useSave reported it */ }
  };

  usePaletteActions(useMemo(() => [
    ...(editable ? [{ id: "page.check", label: t("admin:pageStudio.runCheck"), icon: ShieldCheck, permission: "pages.author", run: () => setTab("validation") }] : []),
    ...(p.status === "Published" ? [{ id: "page.open", label: t("admin:pageStudio.openInWorkspace"), icon: ExternalLink, permission: "pages.use", run: () => openInAssistant(`/p/${encodeURIComponent(p.key)}`) }] : []),
  ], [editable, p.key, p.status, t]));

  useEffect(() => {
    if (!codeDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [codeDirty]);

  const openIssue = (path: string, line?: number | null) => { setOpenFile(path); setFocus({ path, line, n: Date.now() }); setTab("code"); };
  const showAuthor = authorOpen && editable;
  const offerAuthor = authorOpen && canAuthor && !viewing && p.status !== "Draft";

  return (
    <div className="-mt-2">
      <div className="sticky top-14 z-20 -mx-4 mb-4 border-b border-sand-200 bg-background/90 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/pages")} aria-label={t("admin:common.back")}><ArrowLeft className="rtl:rotate-180" /></Button>
          <span className="grid size-9 place-items-center rounded-xl bg-aegold-100 text-aegold-800"><PlatformIcon name={p.icon} fallback={LayoutDashboard} className="size-4" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h1 className="truncate text-base font-semibold">{name}</h1>
              <Badge variant="outline" size="sm" className="font-mono">{p.key}</Badge>
              <Badge variant={statusTone(p.status)} size="sm">v{p.version} · {t(`admin:pageStudio.status.${p.status}`, { defaultValue: p.status })}</Badge>
              {p.validationStatus ? <Badge variant={checkTone(p.validationStatus)} size="sm">{t(`admin:pageStudio.check.${p.validationStatus}`, { defaultValue: p.validationStatus })}</Badge> : null}
              {p.visibility === "Personal" ? <Badge variant="camel" size="sm">{t("admin:pageStudio.personal")}</Badge> : null}
            </div>
            <p className="text-xs text-muted-foreground">{[p.spaces.join(", ") || t("admin:pageStudio.noSpace"), p.slots.map((s) => t(`admin:pageStudio.slots.${s}`, { defaultValue: s })).join(", "), t("admin:pageStudio.updated", { when: formatRelative(p.updatedAt, locale) })].join(" · ")}</p>
          </div>
          <div className="ms-auto flex flex-wrap items-center gap-1">
            {p.status === "Published" && !viewing ? <Button size="sm" variant="ghost" onClick={() => openInAssistant(`/p/${encodeURIComponent(p.key)}`)}><ExternalLink /> {t("admin:pageStudio.openInWorkspace")}</Button> : null}
            <Button size="sm" variant="ghost" onClick={() => void actions.exportZip(p.id, `${p.key}-v${p.version}.zip`).catch((e: unknown) => notifySaveFailed(e, t("admin:common.error")))}><Download /> {t("admin:common.export")}</Button>
            {canAuthor && !viewing && p.status !== "Draft" ? <Button size="sm" variant="gold" loading={actions.draft.isPending} onClick={() => void startDraft()}><PencilLine /> {t("admin:pageStudio.edit")}</Button> : null}
            {editable ? (<>
              <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => void discard()}><Trash2 /> {t("admin:pageStudio.discard")}</Button>
              <Button size="sm" variant="outline" loading={actions.build.isPending} onClick={() => void runBuild()}><Hammer /> {t("admin:pageStudio.build")}</Button>
              {p.visibility !== "Personal" ? <Button size="sm" variant="gold" loading={actions.stage.isPending} onClick={() => void stage()}><Rocket /> {t("admin:pageStudio.stage")}</Button> : null}
              <Button size="icon-sm" variant="ghost" aria-label={authorOpen ? t("admin:pageStudio.hideAuthor") : t("admin:pageStudio.showAuthor")} aria-pressed={authorOpen} onClick={() => setAuthorOpen((o) => !o)}>
                {authorOpen ? <PanelRightClose className="rtl:rotate-180" /> : <PanelRightOpen className="rtl:rotate-180" />}
              </Button>
            </>) : null}
          </div>
        </div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="mt-2">
          <TabsList className="flex-wrap">
            {TABS.map(({ key: k, icon: Icon }) => (
              <TabsTrigger key={k} value={k}><Icon className="size-3.5" /> {t(`admin:pageStudio.tabs.${k}`)}
                {k === "suggestions" && waiting ? <Badge variant="warning" size="sm" className="ms-1">{waiting}</Badge> : null}
                {k === "agents" && detail.agents?.length ? <Badge variant="outline" size="sm" className="ms-1">{detail.agents.length}</Badge> : null}
                {k === "jobs" && detail.jobs?.length ? <Badge variant="outline" size="sm" className="ms-1">{detail.jobs.length}</Badge> : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {viewing ? <InlineAlert tone="info" className="mb-3">{t("admin:pageStudio.viewingOld", { version: p.version })} <button type="button" className="underline" onClick={() => set({ version: null })}>{t("admin:pageStudio.versions.backToNewest")}</button></InlineAlert> : null}
      {!editable && !viewing && canAuthor && p.status === "Published" ? <InlineAlert tone="info" className="mb-3">{t("admin:pageStudio.publishedNote")}</InlineAlert> : null}

      <div className={showAuthor || offerAuthor ? "grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]" : undefined}>
        <div className="min-w-0">
          {tab === "preview" ? <PreviewTab detail={detail} reloadKey={rev} /> : null}
          {tab === "code" ? <CodeTab detail={detail} editable={editable} open={openFile} onOpen={setOpenFile} focus={focus} onSaved={refresh} onDirty={setCodeDirty} /> : null}
          {tab === "data" ? <DataTab detail={detail} /> : null}
          {tab === "agents" ? <AgentsTab detail={detail} onOpenFile={(path) => { setOpenFile(path); setTab("code"); }} onOpenAgent={(k) => navigate(`/agents/${encodeURIComponent(k)}`)} /> : null}
          {tab === "jobs" ? <JobsTab detail={detail} onOpenAutomation={(k) => navigate(`/automations/${encodeURIComponent(k)}`)} /> : null}
          {tab === "versions" ? <VersionsTab detail={newest} viewing={viewing} onView={(id) => set({ version: id })} onRestored={refresh} /> : null}
          {tab === "validation" ? <ValidationTab detail={detail} canRun={canAuthor && !viewing} onOpenIssue={openIssue} onChecked={refresh} /> : null}
          {tab === "suggestions" ? <SuggestionsTab pageKey={p.key} onAccepted={refresh} /> : null}
          {tab === "usage" ? <UsageTab pageKey={p.key} /> : null}
        </div>
        {showAuthor ? (
          <aside className="sticky top-36 h-[calc(100vh-10rem)] min-h-[480px] overflow-hidden rounded-2xl border border-sand-200 bg-white">
            <AuthorPanel key={p.id} definitionId={p.id} pageName={name} initialMessage={build ? p.description ?? null : null}
              selection={tab === "code" ? openFile : null} context={{ studioTab: tab, openFile: tab === "code" ? openFile : null, validation: p.validationStatus ?? null }}
              onChanged={() => { refresh(); if (build) set({ build: null }); }} onOpenLink={(link) => (link.startsWith("/pages/") ? navigate(link) : openInAssistant(link))} />
          </aside>
        ) : null}
        {offerAuthor ? (
          <aside className="sticky top-36 self-start rounded-2xl border border-sand-200 bg-white p-5">
            <h2 className="flex items-center gap-2 text-sm font-semibold"><Wand2 className="size-4 text-aegold-700" /> {t("admin:pageStudio.authorTitle")}</h2>
            <p className="mt-2 text-sm text-sand-700">{t("admin:pageStudio.authorDraftNote")}</p>
            <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
              {(["plan", "edit", "check", "data"] as const).map((k) => <li key={k} className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0 text-success-600" /> {t(`admin:pageStudio.authorCan.${k}`)}</li>)}
            </ul>
            <Button variant="gold" className="mt-4 w-full" loading={actions.draft.isPending} onClick={() => void startDraft()}><PencilLine /> {t("admin:pageStudio.editWithAuthor")}</Button>
          </aside>
        ) : null}
      </div>
      {editable && !authorOpen ? (
        <Button variant="gold" className="fixed bottom-6 end-6 z-30 shadow-gold" onClick={() => setAuthorOpen(true)}><Wand2 /> {t("admin:pageStudio.showAuthor")}</Button>
      ) : null}
    </div>
  );
}
